import { Container, Box, Button, CircularProgress, Grid, Typography } from '@material-ui/core';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { AppShopqrMain } from './AppShopqrMain';
import { useAppDispatch, useAppSelector } from './app/hooks';
import { startServerSession } from './features/question/inputAnswerSlice';
import { cxSurveyPreviewModeHomeq, resetPreviewToInitialState } from './features/preview/PreviewReducer';
import { APP_MODE_V } from '../../user-api/dist/src/dto/shopqr/shopqr-session-dto';
import { AppCouponMain } from './AppCouponMain';
import { MyAppBar } from './features/appbar/MyAppBar';
import { MyFooter } from './features/footer/MyFooter';
import { initDatadog } from './util/monitoring-util';
import { useTranslation } from 'react-i18next';

interface IframeReceiveMessageCxSurvey {
  sender: string;
  message: object;
}

interface DataIframeCxSurvey {
  data: IframeReceiveMessageCxSurvey;
}

function App() {
  const { t } = useTranslation();
  const [reloading, setReloading] = useState(false);

  const dispatch = useAppDispatch();
  const session = useAppSelector(state => {
    return state.inputAnswer.session;
  });
  const isPreviewMode = useAppSelector(state => state.preview.isReviewMode);

  /** Handle received data from CxSurveyAdmin via PostMessage and dispatch data to store */
  const onMessageReceivedFromCxSurvey = useCallback((event: DataIframeCxSurvey) => {
    if (
      event.data &&
      event.data.sender === 'cxSurveyAdmin'
    ) {
      const { message } = event.data;
      dispatch<any>(resetPreviewToInitialState());
      dispatch<any>(cxSurveyPreviewModeHomeq({ message }));
    }
  }, [dispatch]);

  /** Check current window is iframe or not */
  const isIframe = useMemo(() =>
    global.window && window.location !== window.parent.location, []);

  useEffect(() => {
    /** Check open iframe from cxSurvey to preview, otherwise work as normal */
    if (isIframe) {
      window.parent.postMessage({sender: 'IframeHomeq'}, document.referrer);
      window.addEventListener('message', onMessageReceivedFromCxSurvey, false);
    } else {
      dispatch<any>(startServerSession());
    }

    return () => {
      window.removeEventListener('message', onMessageReceivedFromCxSurvey);
    };
  }, [dispatch, isIframe, onMessageReceivedFromCxSurvey]);

   /** Check is previewMode */
  if(isPreviewMode) {
    return (
      <AppShopqrMain />
    );
  }

  const isPending = session.status === 'idle' || session.status === 'pending';

  if (isPending || reloading) {
    return (
      <Box display="flex" position="fixed" left="0" top="0" right="0" bottom="0" alignItems="center" mx={5}>
        <Grid container spacing={0} direction="column" alignItems="center" justify="center">
          <div><CircularProgress /></div>
          <Typography variant="h4">now loading …</Typography>
        </Grid>
        <MyFooter styles={{position: "absolute", bottom: "0"}}/>
      </Box>
    );
  }

  const handleReloadClicked = () => {

    setReloading(true);
    window.location.reload();
  };

  const isError = session.status === 'rejected';
  if (isError) {
    return (
      <Container
        maxWidth={false}
        disableGutters={true}
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden"
        }}
      >
          <MyAppBar />
          <Box mt={2} flexGrow="1" position="relative">
            <Box mx={3} mt={1}>
              <Typography variant="h6">
                <Box fontWeight="fontWeightBold">{t('common.cannot_be_used_at_the_moment')}</Box>
              </Typography>
            </Box>
            <Box mx={3} mt={3}>
              <Typography component="span" style={{whiteSpace:'pre-wrap'}}>
                <Box>{t('common.the_page_may_be_temporarily_unavailable_or_the_link_might_have_been_removed')}</Box>
              </Typography>
            </Box>
            <Button
              style={{
                position: "absolute",
                top: "50%",
                left: "calc(50% - 60px)"
              }}
              variant="contained"
              onClick={handleReloadClicked}
            >
              {t('common.btn.refresh')}
            </Button>
          </Box>
        <MyFooter />
      </Container>
    );
  }

  // pendingではない事が保証されているのでhttpResは必ず存在する
  const httpRes = session.cxHttpRes!;

  if (httpRes.isError) {
    return (
      <Box display="flex" position="fixed" left="0" top="0" right="0" bottom="0" alignItems="center" mx={5}>
        <Grid container spacing={0} direction="column" alignItems="center" justify="center">
          {httpRes.messages.map((value, index) => {
            return <Typography key={index} variant="h6">{value.message}</Typography>
          })}

        </Grid>
        <MyFooter styles={{position: "absolute", bottom: "0"}}/>
      </Box>
    );
  }

  // 情報がきちんと揃っているか確認する
  if (!session.httpResIsValid) {
    return (
      <Box display="flex" position="fixed" left="0" top="0" right="0" bottom="0" alignItems="center" mx={5}>
        <Grid container spacing={0} direction="column" alignItems="center" justify="center">
        <Typography variant="h6">{t('common.error.server_is_unstable')}</Typography>
          <Typography variant="h6">{t('common.please_wait_a_while_and_then_reload_the_app')}</Typography>
          <Button variant="contained" onClick={handleReloadClicked}>{t('common.btn.refresh')}</Button>
        </Grid>
        <MyFooter styles={{position: "absolute", bottom: "0"}}/>
      </Box>
    );
  }

  // AppModeが店舗QRかクーポンかで場合分け
  switch (httpRes.data?.cxAppSession.appMode) {
    case APP_MODE_V.shopqr:
      return (
        <AppShopqrMain />
      )

    case APP_MODE_V.coupon:
      return(
        <AppCouponMain />
      )

    default:
      return (
        <></>
      )
  }
}

initDatadog();
export default App;
